"use client";

import { useState, useTransition } from "react";
import { Heart } from "lucide-react";

/**
 * The heart (docs/ux/flows/consumer-favorites.md, H35).
 *
 * **Optimistic, and rolled back on failure.** The fill happens on tap, before
 * the server answers, because a heart that waits for a round trip feels broken.
 * If the write fails the fill is undone and a message appears: nothing is
 * queued to apply later, since a favorite that silently lands minutes
 * afterwards is worse than one that visibly did not.
 *
 * **It sends the state it wants, never "flip it".** Two taps racing from two
 * devices, or an offline retry, both settle on the same answer this way. See
 * the note in `favorites/domain/favoriteForm.ts`.
 *
 * **It works inside a card that is already a link.** A `<button>` nested in an
 * `<a>` is invalid markup, so on the search cards this renders as a sibling
 * positioned over the card, and the click is stopped before it reaches the
 * anchor underneath (the same problem `CardMapLink` solves the same way).
 *
 * A logged-out visitor gets the log-in prompt instead of a write, and the
 * favorite is deliberately **not** applied on return: carrying a pending action
 * through an auth redirect means a silent write the person never knowingly
 * repeated.
 *
 * **The write and the navigation arrive as props** (R22 T12). They were the
 * only two things that ever differed between the two frontends this file was
 * written to serve, and keeping them out is why everything above could stay in
 * one place: "optimistic and rolled back" is easy to get subtly wrong twice.
 */
export function FavoriteHeart({
  businessId,
  initialFavorite,
  signedIn,
  next,
  className,
  save,
  push,
}: {
  businessId: string;
  initialFavorite: boolean;
  /** False for a logged-out visitor: the heart becomes a log-in prompt. */
  signedIn: boolean;
  /** Where to come back to after logging in (guests only). */
  next: string;
  className?: string;
  /** Persist the desired state. Resolves false when the write was refused. */
  save: (businessId: string, favorite: boolean) => Promise<boolean>;
  /** Navigate, for the logged-out prompt. The host's router, injected. */
  push: (url: string) => void;
}) {
  const [favorite, setFavoriteState] = useState(initialFavorite);
  const [failed, setFailed] = useState(false);
  const [pending, startTransition] = useTransition();

  const base =
    "inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-line bg-surface/90 backdrop-blur transition hover:border-accent/40 disabled:cursor-default";

  function act() {
    if (!signedIn) {
      push(`/login?next=${encodeURIComponent(next)}`);
      return;
    }
    const desired = !favorite;
    setFavoriteState(desired); // optimistic
    setFailed(false);
    startTransition(async () => {
      const saved = await save(businessId, desired);
      if (!saved) {
        setFavoriteState(!desired); // roll back
        setFailed(true);
      }
    });
  }

  const label = !signedIn
    ? "Log in to save this clinic"
    : favorite
      ? "Remove from saved clinics"
      : "Save this clinic";

  return (
    <span className={className}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={signedIn ? favorite : undefined}
        title={label}
        disabled={pending}
        onClick={(e) => {
          // The card underneath is a link; this tap is not for it.
          e.preventDefault();
          e.stopPropagation();
          act();
        }}
        className={base}
      >
        <Heart
          className={`size-4.5 transition ${
            favorite ? "fill-accent text-accent" : "text-muted"
          }`}
          aria-hidden
          strokeWidth={1.8}
        />
      </button>
      {failed && (
        <span role="status" className="ms-2 text-xs text-muted">
          Couldn&apos;t update this. Try again.
        </span>
      )}
    </span>
  );
}
